import Image from 'next/image';

import { Box, Divider, Flex, Heading, LinkBox, LinkOverlay, Text } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { StarIcon } from '~/components/icons';
import { FREE_COURSE_SLUG } from '~/features/ai-marketer/constants';
import { trackEvent } from '~/features/analytics/track-event';

const CTA_HREF = `/course/${FREE_COURSE_SLUG}`;
const BG_SRC = '/images/experiments/ab_plp_redesign_2025/ai-marketer-bg.webp';
const BG_SRC_MOBILE = '/images/experiments/ab_plp_redesign_2025/ai-marketer-bg-mobile.webp';
const ROCKET_SRC = '/images/experiments/ab_plp_redesign_2025/ai-marketer-rocket.png';

const handleCtaClick = () =>
  trackEvent({
    type: 'click',
    name: 'Homepage CTA Clicked',
    label: 'enroll_now_clicked',
    value: 'AI Marketer',
    additionalAttributes: { degree_key: 'cd15914' },
  });

/**
 * Homepage Udacity Impact carousel — AI Marketer free course (Figma 2595:26543 / 2595:26578).
 */
export function AiMarketerPromoCard() {
  return (
    <LinkBox
      aria-labelledby='ai-marketer-title'
      as='aside'
      bg='#00050E'
      borderRadius='8px'
      data-testid='ai-marketer-promo-card'
      height='100%'
      minHeight='100%'
      overflow='hidden'
      padding={{ base: '32px', md: '48px' }}
      position='relative'
      width='100%'
    >
      <Box display={{ base: 'block', md: 'none' }} inset={0} pointerEvents='none' position='absolute'>
        <Image
          alt=''
          quality={85}
          sizes='100vw'
          src={BG_SRC_MOBILE}
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          fill
        />
      </Box>
      <Box display={{ base: 'none', md: 'block' }} inset={0} pointerEvents='none' position='absolute'>
        <Image
          alt=''
          quality={85}
          sizes='(max-width: 1120px) 100vw, 1056px'
          src={BG_SRC}
          style={{ objectFit: 'cover', objectPosition: 'right center' }}
          fill
        />
      </Box>
      <Box
        inset={0}
        pointerEvents='none'
        position='absolute'
        bgGradient={{
          base: 'linear(to-br, #00050E 15%, rgba(0, 5, 14, 0.55) 55%, transparent 100%)',
          md: 'linear(to-r, #00050E 0%, #00050E 42%, transparent 88%)',
        }}
      />
      <Box
        height={{ base: '130px', md: '225px' }}
        pointerEvents='none'
        position='absolute'
        right={{ base: '-33px', md: '48px' }}
        top={{ base: '116px', md: '28px' }}
        width={{ base: '146px', md: '253px' }}
      >
        <Image
          alt=''
          sizes='(max-width: 767px) 146px, 253px'
          src={ROCKET_SRC}
          style={{ objectFit: 'contain', objectPosition: 'right center' }}
          fill
        />
      </Box>

      <Flex
        alignItems='flex-start'
        flexDirection='column'
        gap={{ base: 4, md: 6 }}
        height='100%'
        maxWidth={{ base: '100%', md: '640px' }}
        position='relative'
        width='100%'
      >
        <Flex alignItems='center' gap={{ base: 3, md: 4 }} width='100%'>
          <Flex alignItems='center' gap={2}>
            <StarIcon color='orange.300' height='16px' width='16px' />
            <Text
              color='orange.300'
              fontFamily='condensed'
              fontSize={{ base: '13px', md: '15px' }}
              fontWeight='500'
              letterSpacing={{ base: '1.04px', md: '0.9px' }}
              lineHeight='1'
              textTransform='uppercase'
              whiteSpace='nowrap'
            >
              New Free Course
            </Text>
          </Flex>
          <Divider borderColor='orange.300' flex={1} opacity={0.45} />
        </Flex>

        <Flex alignItems='flex-start' flexDirection='column' gap={{ base: 3, md: 3 }} maxWidth={{ md: '573px' }}>
          <Heading
            as='h2'
            color='white'
            fontSize={{ base: '1.75rem', md: '2.25rem' }}
            fontWeight='600'
            id='ai-marketer-title'
            letterSpacing='-0.36px'
            lineHeight={{ base: '1.15', md: '1.1' }}
          >
            <LinkOverlay href={CTA_HREF} onClick={handleCtaClick}>
              Put your marketing on AI autopilot
            </LinkOverlay>
          </Heading>
          <Text color='orange.200' fontSize={{ base: '14px', md: 'md' }} lineHeight={{ base: '1.4', md: '1.5' }}>
            Build reusable Claude skills and automate everyday marketing tasks.
          </Text>
        </Flex>

        <Box mt='auto' width={{ base: '100%', md: 'auto' }}>
          <ButtonLink
            buttonProps={{
              bg: 'orange.700',
              borderRadius: '4px',
              color: 'white',
              fontSize: '15px',
              fontWeight: '500',
              height: '45px',
              px: '24px',
              position: 'relative',
              width: { base: '100%', md: '152px' },
              zIndex: 1,
              _hover: { bg: 'orange.900' },
              onClick: handleCtaClick,
            }}
            linkProps={{
              href: CTA_HREF,
            }}
          >
            Enroll for Free
          </ButtonLink>
        </Box>
      </Flex>
    </LinkBox>
  );
}
